import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useAdminAuth } from '../../context/AdminAuthContext';
import ContactSubmissionDetailModal from '../components/ContactSubmissionDetailModal';
import {
  fetchContactSubmissions,
  updateContactSubmissionStatus,
  addContactSubmissionNote,
} from '../services/contactSubmissionsApi';
import { TableSkeleton } from '../../../components/loading/skeletons';
import '../styles/ContactSubmissions.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'read', label: 'Read' },
  { value: 'replied', label: 'Replied' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_ACTIONS = [
  { value: 'read', label: 'Mark Read' },
  { value: 'replied', label: 'Mark Replied' },
  { value: 'archived', label: 'Archive' },
  { value: 'new', label: 'Mark New' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'MMM d, yyyy h:mm a');
};

const getStatusClass = (status) => {
  const safe = String(status || 'new').toLowerCase();
  return `contactSubmissionStatus contactSubmissionStatus--${safe}`;
};

const stopRowClick = (event) => {
  event.stopPropagation();
};

const ContactSubmissionsPage = () => {
  const { admin } = useAdminAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchContactSubmissions();
      if (result?.success && Array.isArray(result.submissions)) {
        setSubmissions(result.submissions);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      setError(err.message || 'Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (admin?.uid) {
      loadSubmissions();
    }
  }, [admin?.uid]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (!selectedSubmission) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setSelectedSubmission(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedSubmission]);

  const filteredSubmissions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return submissions.filter((submission) => {
      const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
      if (!matchesStatus) return false;
      if (!query) return true;
      return [
        submission.name,
        submission.email,
        submission.phone,
        submission.subject,
        submission.message,
      ].some((field) => String(field || '').toLowerCase().includes(query));
    });
  }, [submissions, searchTerm, statusFilter]);

  const newCount = useMemo(
    () => submissions.filter((submission) => submission.status === 'new').length,
    [submissions]
  );

  const total = filteredSubmissions.length;
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, total);
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const rowsToRender = filteredSubmissions.slice(startIndex, endIndex);

  const updateSubmissionInState = (submissionId, updates) => {
    setSubmissions((prev) => prev.map((submission) => (
      submission.id === submissionId ? { ...submission, ...updates } : submission
    )));
    setSelectedSubmission((current) => (
      current?.id === submissionId ? { ...current, ...updates } : current
    ));
  };

  const handleStatusChange = async (submissionId, status) => {
    try {
      setUpdatingId(submissionId);
      await updateContactSubmissionStatus({ submissionId, status });
      updateSubmissionInState(submissionId, { status });
    } catch (err) {
      setError(err.message || 'Failed to update submission status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddNote = async (submissionId, text) => {
    try {
      setSavingNoteId(submissionId);
      setError(null);
      const result = await addContactSubmissionNote({ submissionId, text });
      if (result?.success && result.note) {
        setSubmissions((prev) => prev.map((submission) => {
          if (submission.id !== submissionId) return submission;
          const existingNotes = Array.isArray(submission.adminNotes) ? submission.adminNotes : [];
          return {
            ...submission,
            adminNotes: [result.note, ...existingNotes],
          };
        }));
        setSelectedSubmission((current) => {
          if (current?.id !== submissionId) return current;
          const existingNotes = Array.isArray(current.adminNotes) ? current.adminNotes : [];
          return {
            ...current,
            adminNotes: [result.note, ...existingNotes],
          };
        });
        return true;
      }
      throw new Error('Failed to add note');
    } catch (err) {
      setError(err.message || 'Failed to add note');
      return false;
    } finally {
      setSavingNoteId(null);
    }
  };

  const openSubmission = (submission) => {
    setSelectedSubmission(submission);
  };

  if (loading) {
    return (
      <div className="admin-page contactSubmissionsPage">
        <div className="admin-page__header">
          <div>
            <h1 className="admin-page__title">Contact Messages</h1>
            <p className="admin-page__subtitle">Website contact form submissions from neatschedule.com.</p>
          </div>
        </div>
        <div className="contactSubmissionsCard">
          <TableSkeleton rows={8} columns={[180, 200, 140, 120, 160]} label="Loading contact messages" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page contactSubmissionsPage">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Contact Messages</h1>
          <p className="admin-page__subtitle">Website contact form submissions from neatschedule.com.</p>
        </div>
        <div className="contactSubmissionsHeaderRight">
          <span className="contactSubmissionsPill">{newCount} new</span>
          <span className="contactSubmissionsPill">{total} shown</span>
        </div>
      </div>

      {error ? (
        <div className="alert alertError" role="alert" aria-live="polite" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      <div className="contactSubmissionsCard">
        <div className="contactSubmissionsFilters">
          <div className="contactSubmissionsField">
            <label htmlFor="contactSubmissionSearch">Search</label>
            <input
              id="contactSubmissionSearch"
              type="search"
              placeholder="Search by name, email, phone, or subject…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="contactSubmissionsField" style={{ flex: '0 1 220px' }}>
            <label htmlFor="contactSubmissionStatus">Status</label>
            <select
              id="contactSubmissionStatus"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table contactSubmissionsTable" aria-label="Contact submissions table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Status</th>
              <th>Email</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rowsToRender.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="contactSubmissionsEmpty">
                    No contact submissions found{searchTerm || statusFilter !== 'all' ? ' for these filters.' : '.'}
                  </div>
                </td>
              </tr>
            ) : (
              rowsToRender.map((submission) => {
                const isUpdating = updatingId === submission.id;

                return (
                  <tr
                    key={submission.id}
                    className="contactSubmissionsRow"
                    onClick={() => openSubmission(submission)}
                  >
                    <td>
                      <span className="contactSubmissionsPrimaryText">
                        {formatDateTime(submission.createdAt)}
                      </span>
                    </td>
                    <td>
                      <span className="contactSubmissionsPrimaryText">{submission.name || '—'}</span>
                    </td>
                    <td>
                      <span className={getStatusClass(submission.status)}>
                        {submission.status || 'new'}
                      </span>
                    </td>
                    <td>
                      <span className={`contactSubmissionEmailSent contactSubmissionEmailSent--${submission.emailSent ? 'yes' : 'no'}`}>
                        {submission.emailSent ? 'Sent' : 'Failed'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }} onClick={stopRowClick}>
                      <div className="contactSubmissionActions">
                        {STATUS_ACTIONS.filter((action) => action.value !== submission.status).map((action) => (
                          <button
                            key={action.value}
                            type="button"
                            className="contactSubmissionBtn"
                            disabled={isUpdating}
                            onClick={() => handleStatusChange(submission.id, action.value)}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="contactSubmissionsFooter">
          <div className="contactSubmissionsFooterLeft">
            <span>
              Showing <strong>{total === 0 ? 0 : startIndex + 1}</strong>–<strong>{endIndex}</strong> of{' '}
              <strong>{total}</strong>
            </span>
            <span>
              Rows:
              <select
                className="contactSubmissionsSelect"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                aria-label="Rows per page"
                style={{ marginLeft: 8 }}
              >
                {[5, 10, 25].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </span>
          </div>
          <div className="contactSubmissionsPager" aria-label="Pagination">
            <button
              type="button"
              className="contactSubmissionsPagerBtn"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={page <= 0}
            >
              Prev
            </button>
            <span className="contactSubmissionsPill">
              Page {Math.min(page + 1, pageCount)} / {pageCount}
            </span>
            <button
              type="button"
              className="contactSubmissionsPagerBtn"
              onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
              disabled={endIndex >= total}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ContactSubmissionDetailModal
        submission={selectedSubmission}
        open={Boolean(selectedSubmission)}
        onClose={() => setSelectedSubmission(null)}
        onStatusChange={handleStatusChange}
        onAddNote={handleAddNote}
        updating={Boolean(selectedSubmission && updatingId === selectedSubmission.id)}
        savingNote={Boolean(selectedSubmission && savingNoteId === selectedSubmission.id)}
        formatDateTime={formatDateTime}
      />
    </div>
  );
};

export default ContactSubmissionsPage;
